import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "@capacit-com/lara-law-shared-ui";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@capacit-com/lara-law-shared-ui";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@capacit-com/lara-law-shared-ui";
import { FC, useMemo, useState } from "react";
import { HourlyStats } from "@/models/statistic/Statistic";

interface LineChartProps {
  graphStats?: HourlyStats[];
  isLoading?: boolean;
  type: "conversations" | "messages";
  title?: string;
  description?: string;
}

const LineChartComponent: FC<LineChartProps> = ({
  graphStats,
  isLoading,
  type,
  title,
  description,
}) => {
  const [diffDaysValue, setDiffDaysValue] = useState(0);
  // Add this function to determine date format based on time span
  const getDateFormat = (dates: string[]): Intl.DateTimeFormatOptions => {
    if (!dates.length) return {};

    const start = new Date(dates[0]);
    const end = new Date(dates[dates.length - 1]);
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    setDiffDaysValue(diffDays);

    // Check if data points are less than 24 hours apart
    if (dates.length >= 2) {
      const first = new Date(dates[0]);
      const second = new Date(dates[1]);
      const timeDiff = second.getTime() - first.getTime();
      const hoursDiff = timeDiff / (1000 * 60 * 60);

      if (hoursDiff < 24) {
        return {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        };
      }
    }

    if (diffDays <= 2) {
      return {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      };
    } else if (diffDays <= 30) {
      return {
        month: "short",
        day: "numeric",
      };
    } else {
      return {
        year: "numeric",
        month: "short",
      };
    }
  };

  const formattedData = useMemo(
    () =>
      graphStats?.map((stat) => ({
        date: stat.hour,
        value:
          type === "conversations"
            ? stat.nonWordConversationCount
            : stat.nonWordMessageCount,
        wordValue:
          type === "conversations"
            ? stat.wordConversationCount
            : stat.wordMessageCount,
      })) ?? [],
    [graphStats, type]
  );

  const dateFormat = useMemo(
    () => getDateFormat(formattedData.map((d) => d.date)),
    [formattedData]
  );

  // Add this function to calculate the maximum value with padding
  const getYAxisDomain = useMemo(() => {
    if (!formattedData.length) return [0, 5];

    const maxValue = Math.max(
      ...formattedData.map((d) => Math.max(d.value ?? 0, d.wordValue ?? 0))
    );

    return [0, maxValue];
  }, [formattedData]);

  const chartConfig = {
    value: {
      label: type === "conversations" ? "Samtaler" : "Beskeder",
      color: "var(--chart-1)",
    },
    wordValue: {
      label: type === "conversations" ? "Word Samtaler" : "Word Beskeder",
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig;

  return (
    <Card className="rounded-lg shadow-md">
      <CardHeader className="flex items-center gap-2 space-y-0 py-5 sm:flex-row">
        <div className="grid flex-1 gap-1 text-center sm:text-left">
          <CardTitle>
            {title ||
              (type === "conversations"
                ? "Samtaler over tid"
                : "Beskeder over tid")}
          </CardTitle>
          <CardDescription>
            {isLoading
              ? "Indlæser..."
              : description ||
                `Viser ${
                  type === "conversations" ? "samtaler" : "beskeder"
                } for den valgte periode`}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="fillDesktop" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--chart-1)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--chart-1)"
                  stopOpacity={0.1}
                />
              </linearGradient>
              <linearGradient id="fillMobile" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--chart-2)"
                  stopOpacity={0.8}
                />
                <stop
                  offset="95%"
                  stopColor="var(--chart-2)"
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={diffDaysValue <= 30 ? 1 : 32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("da-DK", dateFormat);
              }}
            />
            <YAxis
              domain={getYAxisDomain}
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              allowDecimals={false}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(label) =>
                    typeof label === "string"
                      ? new Date(label).toLocaleDateString("da-DK", dateFormat)
                      : label
                  }
                  indicator="dot"
                />
              }
            />
            <Area
              dataKey="value"
              type="bump"
              fill="url(#fillDesktop)"
              stroke="var(--chart-1)"
            />
            {graphStats?.some(
              (stat) =>
                stat.wordMessageCount !== undefined ||
                stat.wordConversationCount !== undefined
            ) && (
              <Area
                dataKey="wordValue"
                type="bump"
                fill="url(#fillMobile)"
                stroke="var(--chart-2)"
              />
            )}
            <ChartLegend content={<ChartLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default LineChartComponent;
